/**
 * Meta's List view: tasks grouped into collapsible sections, each with a
 * "+ Task · Milestone · Section" footer. Unread rows are bold with a blue bar,
 * closed rows are struck through, and the star column feeds My To Do List.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Clock, Diamond, Github, Star, Trash2 } from 'lucide-react';
import {
  type Task,
  useCreateSectionMutation,
  useDeleteSectionMutation,
  useStarTaskMutation,
  useUpdateSectionMutation,
} from '@/client/api/tasks/tasks';
import {
  Avatar,
  fmtUpdated,
  groupBySection,
  isClosed,
  isDueSoon,
  isOverdue,
  personName,
  PriorityPill,
  StatusPill,
  type TaskViewProps,
} from './taskUi';

const InlineInput: React.FC<{ placeholder: string; onSubmit: (v: string) => Promise<void> | void; onDone: () => void; keepOpen?: boolean }> = ({
  placeholder,
  onSubmit,
  onDone,
  keepOpen,
}) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!value.trim()) return onDone();
    setBusy(true);
    setError('');
    try {
      await onSubmit(value.trim());
      setValue('');
      if (!keepOpen) onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="px-3 py-1.5">
      <input
        autoFocus
        aria-label={placeholder}
        value={value}
        disabled={busy}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void submit();
          if (e.key === 'Escape') onDone();
        }}
        onBlur={() => !value.trim() && onDone()}
        placeholder={placeholder}
        className="h-8 w-full rounded border border-blue-300 px-2 text-sm outline-none"
      />
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

const ListView: React.FC<TaskViewProps> = ({ tasks, loading, meta, project, selectedId, onOpen, onCreate }) => {
  const star = useStarTaskMutation();
  const createSection = useCreateSectionMutation();
  const renameSection = useUpdateSectionMutation();
  const deleteSection = useDeleteSectionMutation();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [adding, setAdding] = useState<{ key: string; kind: 'task' | 'milestone' | 'section' } | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const people = meta?.people;
  const groups = groupBySection(tasks, project);
  const showHeaders = !!project || groups.length > 1;

  if (loading && tasks.length === 0) {
    return (
      <div className="grid gap-2 p-3">
        {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-9 animate-pulse rounded bg-gray-100" />)}
      </div>
    );
  }

  const row = (t: Task) => {
    const closed = isClosed(t.status);
    const selected = t.id === selectedId;
    return (
      <tr
        key={t.id}
        tabIndex={0}
        aria-selected={selected}
        onClick={() => onOpen(t)}
        onKeyDown={(e) => e.key === 'Enter' && onOpen(t)}
        className={`cursor-pointer border-b border-gray-100 outline-none focus-visible:bg-blue-50 ${selected ? 'bg-blue-100/70' : 'hover:bg-gray-50'}`}
      >
        <td className={`w-0 p-0 ${t.unread ? 'border-l-[3px] border-blue-600' : 'border-l-[3px] border-transparent'}`} />
        <td className="w-44 px-2 py-1.5">
          <span className="flex items-center gap-2">
            <Avatar people={people} uid={t.ownerUid} size={22} />
            <span className="hidden truncate text-gray-700 xl:inline">{t.ownerUid == null ? '' : personName(people, t.ownerUid)}</span>
          </span>
        </td>
        <td className="max-w-0 px-2 py-1.5">
          <span className="flex min-w-0 items-center gap-1.5">
            {t.isMilestone && <Diamond className="h-3.5 w-3.5 shrink-0 fill-blue-500 text-blue-500" aria-label="Milestone" />}
            <span className={`truncate ${t.unread ? 'font-semibold text-gray-900' : 'text-gray-800'} ${closed ? 'text-gray-400 line-through' : ''}`}>
              {t.title}
            </span>
            {t.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="hidden shrink-0 rounded bg-gray-100 px-1 text-[11px] text-gray-500 lg:inline">{tag}</span>
            ))}
            {t.githubIssues.length > 0 && <Github className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-label="Linked to GitHub" />}
          </span>
        </td>
        <td className="w-20 px-2 py-1.5"><PriorityPill priority={t.priority} /></td>
        <td className="w-36 whitespace-nowrap px-2 py-1.5 text-gray-600">{fmtUpdated(t.updatedAt)}</td>
        <td className="w-24 px-2 py-1.5"><StatusPill status={t.status} /></td>
        <td className="w-14 px-1 py-1.5">
          <span className="flex items-center justify-end gap-1">
            {(isOverdue(t) || isDueSoon(t)) && (
              <Clock
                className={`h-3.5 w-3.5 ${isOverdue(t) ? 'text-red-500' : 'text-amber-500'}`}
                aria-label={isOverdue(t) ? `Overdue (${t.dueDate})` : `Due ${t.dueDate}`}
              />
            )}
            <button
              type="button"
              aria-label={t.starred ? 'Unstar' : 'Star'}
              onClick={(e) => {
                e.stopPropagation();
                star.mutate({ id: t.id, starred: !t.starred });
              }}
              className="rounded p-0.5 hover:bg-gray-200"
            >
              <Star className={`h-4 w-4 ${t.starred ? 'fill-blue-600 text-blue-600' : 'text-gray-300'}`} />
            </button>
          </span>
        </td>
      </tr>
    );
  };

  return (
    <div className="min-w-0">
      <table className="w-full table-fixed text-sm">
        <thead className="sticky top-0 z-10 bg-white">
          <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-600">
            <th className="w-0 p-0" />
            <th className="w-44 px-2 py-2">Owner</th>
            <th className="px-2 py-2">Title</th>
            <th className="w-20 px-2 py-2">Priority</th>
            <th className="w-36 px-2 py-2">Updated</th>
            <th className="w-24 px-2 py-2">Status</th>
            <th className="w-14 px-1 py-2 text-right"><Star className="ml-auto h-3.5 w-3.5 text-gray-400" aria-label="Starred" /></th>
          </tr>
        </thead>
        {groups.map((g) => {
          const key = g.id ?? 'none';
          const isCollapsed = collapsed[key];
          return (
            <tbody key={key} className="group/section">
              {showHeaders && (
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td colSpan={7} className="px-2 py-1.5">
                    <span className="flex items-center gap-1">
                      <button
                        type="button"
                        aria-expanded={!isCollapsed}
                        onClick={() => setCollapsed((c) => ({ ...c, [key]: !isCollapsed }))}
                        className="flex items-center gap-1 text-sm font-semibold text-gray-900"
                      >
                        {isCollapsed ? <ChevronRight className="h-4 w-4 text-gray-500" /> : <ChevronDown className="h-4 w-4 text-gray-500" />}
                        {renaming === g.id && g.id ? null : g.name}
                      </button>
                      {renaming === g.id && g.id && (
                        <input
                          autoFocus
                          aria-label="Section name"
                          defaultValue={g.name}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              const v = (e.target as HTMLInputElement).value.trim();
                              if (v) renameSection.mutate({ id: g.id!, name: v });
                              setRenaming(null);
                            }
                            if (e.key === 'Escape') setRenaming(null);
                          }}
                          onBlur={() => setRenaming(null)}
                          className="h-6 rounded border border-blue-300 px-1 text-sm font-semibold outline-none"
                        />
                      )}
                      <span className="ml-1 text-xs text-gray-500">{g.tasks.length}</span>
                      {g.id && (
                        <span className="ml-auto hidden items-center gap-2 text-xs group-hover/section:flex">
                          <button type="button" className="text-blue-600 hover:underline" onClick={() => setRenaming(g.id)}>Rename</button>
                          <button
                            type="button"
                            aria-label={`Delete section ${g.name}`}
                            className="text-gray-400 hover:text-red-600"
                            onClick={() =>
                              window.confirm(`Delete the section "${g.name}"? Its tasks stay in the project.`) && deleteSection.mutate(g.id!)
                            }
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      )}
                    </span>
                  </td>
                </tr>
              )}
              {!isCollapsed && g.tasks.map(row)}
              {!isCollapsed && (
                <tr>
                  <td colSpan={7} className="p-0">
                    {adding?.key === key && adding.kind !== 'section' ? (
                      <InlineInput
                        keepOpen
                        placeholder={adding.kind === 'milestone' ? 'Milestone title' : 'Task title'}
                        onSubmit={(title) => onCreate({ title, sectionId: g.id, isMilestone: adding.kind === 'milestone' })}
                        onDone={() => setAdding(null)}
                      />
                    ) : adding?.key === key && adding.kind === 'section' && project ? (
                      <InlineInput
                        placeholder="Section name"
                        onSubmit={async (name) => {
                          await createSection.mutateAsync({ projectId: project.id, name });
                        }}
                        onDone={() => setAdding(null)}
                      />
                    ) : (
                      <div className="flex items-center gap-1 px-3 py-1.5 text-xs text-blue-600">
                        <span className="text-base leading-none">+</span>
                        <button type="button" className="hover:underline" onClick={() => setAdding({ key, kind: 'task' })}>Task</button>
                        <span className="text-gray-300">·</span>
                        <button type="button" className="hover:underline" onClick={() => setAdding({ key, kind: 'milestone' })}>Milestone</button>
                        {project && (
                          <>
                            <span className="text-gray-300">·</span>
                            <button type="button" className="hover:underline" onClick={() => setAdding({ key, kind: 'section' })}>Section</button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          );
        })}
      </table>
      {!loading && tasks.length === 0 && (
        <p className="px-4 py-10 text-center text-sm text-gray-400">No tasks match. Add one with “+ Task”, or clear the filters.</p>
      )}
    </div>
  );
};

export default ListView;
